import { PageShell } from '@/components/layout/PageShell';
import { APP_NAME, CREDITS } from '@/lib/constants';
import { sectionNumber } from '@/lib/utils';

export function PrivacyPage() {
  return (
    <PageShell>
      <article className="mx-auto max-w-3xl pb-24 pt-8">
        <p className="marginalia">
          <span className="mr-2">{sectionNumber(2)}</span>
          privacy policy
        </p>
        <h1 className="mt-4 font-serif text-display-lg text-ink">
          What we collect, why, and for how long.
        </h1>
        <p className="mt-4 font-mono text-xs text-graphite">
          Effective on first use of this service.
        </p>

        <div className="mt-12 h-px bg-ink/20" />

        <div className="prose-editorial mt-10 space-y-10 text-base leading-relaxed text-ink">
          <section>
            <h2 className="font-serif text-2xl italic text-ink">
              1. &nbsp;Scope.
            </h2>
            <p className="mt-3 text-graphite">
              This policy describes how {APP_NAME} handles the information
              you provide while using the tool. It is not a commercial
              privacy policy; it is a research privacy policy, drafted
              by the authors.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl italic text-ink">
              2. &nbsp;What we collect.
            </h2>
            <ul className="mt-3 space-y-2 text-graphite">
              <li>
                <span className="text-ochre font-mono text-xs">¶</span>&nbsp;
                Account data: email address, password hash, account creation
                timestamp.
              </li>
              <li>
                <span className="text-ochre font-mono text-xs">¶</span>&nbsp;
                Session data: an active session identifier, to enforce the
                single-session rule.
              </li>
              <li>
                <span className="text-ochre font-mono text-xs">¶</span>&nbsp;
                Project data: project names, descriptions, tags, questionnaire
                answers, uploaded sketches, and labels you assign to each
                sketch.
              </li>
              <li>
                <span className="text-ochre font-mono text-xs">¶</span>&nbsp;
                Pipeline telemetry: timestamps and durations for each agent
                stage and checkpoint, counts of revisions, counts of
                validator violations, and token usage per call.
              </li>
              <li>
                <span className="text-ochre font-mono text-xs">¶</span>&nbsp;
                Artefacts: intent descriptors, component trees, hi-fi images,
                generated assets, and zipped codebases.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-2xl italic text-ink">
              3. &nbsp;Why we collect it.
            </h2>
            <p className="mt-3 text-graphite">
              To operate the tool for you, to generate your outputs, and to
              study \u2014 in aggregate \u2014 how an agentic, UX-law-aware
              pipeline compares against commercial vibe-coding and
              specialised designer tools. Telemetry is used in aggregate
              for the paper; no participant is named without consent.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl italic text-ink">
              4. &nbsp;Who we share it with.
            </h2>
            <p className="mt-3 text-graphite">
              Your sketches and questionnaire answers are transmitted to
              Anthropic (for Claude calls) and Google (for Gemini calls) as
              part of normal inference. We do not sell data. We do not share
              it with advertisers. The researchers and their designated
              reviewers are the only parties with access to identifiable
              telemetry; debrief transcripts are anonymised before
              any publication.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl italic text-ink">
              5. &nbsp;Retention.
            </h2>
            <p className="mt-3 text-graphite">
              Account data is retained while the account is active. Project
              data and artefacts are retained until you delete them or until
              thirty days after account deletion. Research telemetry
              associated with consented participants is retained for up to two years
              after publication, then deleted.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl italic text-ink">
              6. &nbsp;Your rights.
            </h2>
            <p className="mt-3 text-graphite">
              You may request a copy of your data, ask for corrections, or
              ask for deletion at any time. Deletion of your participation
              does not retract analyses already published, but it does
              remove your data from any ongoing or future analyses.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl italic text-ink">
              7. &nbsp;Security.
            </h2>
            <p className="mt-3 text-graphite">
              Data at rest lives in Convex Cloud, which encrypts stored data.
              Data in transit uses HTTPS. Passwords are hashed; we never
              store plaintext. Single-session enforcement is implemented
              server-side and cannot be bypassed from the client.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl italic text-ink">
              8. &nbsp;Contact.
            </h2>
            <p className="mt-3 text-graphite">
              Privacy questions, data-access requests, and deletion requests
              should be directed to{' '}
              {CREDITS.map((c, i) => (
                <span key={c.url}>
                  <a
                    href={c.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="underline decoration-ink/30 underline-offset-4 hover:decoration-ochre hover:text-ochre"
                  >
                    {c.name}
                  </a>
                  {i < CREDITS.length - 1 ? ' or ' : '.'}
                </span>
              ))}
            </p>
          </section>
        </div>
      </article>
    </PageShell>
  );
}
